/**
 * Invoice & Receipt review console (admin → Tools).
 *
 * Two fast worklists for root / sales_supervisor / billing_supervisor:
 *   - Invoices to approve — corporate invoices, and any invoice raised by someone
 *     other than root / finance_manager, awaiting the approval gate.
 *   - Receipts to validate — nightly-drafted receipts (P7) awaiting release.
 *
 * Mirrors the CRM quote-approvals inbox. Everything a reviewer needs is on the
 * row; approve/reject and release/hold act inline; a nav badge shows the count.
 */
import { escapeHtml, formatDateTimeShort } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';
import { openInvoicePreview } from './invoice-preview.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);

let currentTab = 'invoices';
let allInvoices = [];       // last-loaded set for the "All invoices" tab (preview lookup)
let pendingApprovals = [];  // last-loaded "Invoices to approve" items (preview + expand lookup)
const billingCache = new Map();   // porta_customer_id → live billing summary (per session)

const STATUS_CHIP = { awaiting_approval: 'warn', issued: 'ok', sent: 'ok', draft: '', rejected: 'danger', void: 'muted' };
function statusChip(inv) {
  const label = inv.pending_approval ? 'Awaiting approval' : (inv.status || '').replace(/_/g, ' ');
  const cls = inv.pending_approval ? 'warn' : (STATUS_CHIP[inv.status] || '');
  return `<span class="badge ${cls}">${escapeHtml(label || '—')}</span>`;
}

function money(cur, v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return `${cur || 'GMD'} ${Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v}`;
}

export async function mountReview(container) {
  container = container || document.getElementById('invoicingReviewView');
  if (!container) return;
  container.innerHTML = `
    <div class="crm-detail-header" style="margin-bottom:10px;">
      <strong style="font-size:15px;">Invoice &amp; receipt review</strong>
      <span class="crm-detail-sub" style="margin-left:8px;">Approve invoices before they are sent; validate receipts before release</span>
      <button class="btn ghost" data-review-refresh style="margin-left:auto;">↻ Refresh</button>
    </div>
    <div class="row" style="gap:6px; margin-bottom:10px;">
      <button class="btn" data-tab="invoices">Invoices to approve <span class="badge" data-count="invoices" style="display:none;"></span></button>
      <button class="btn" data-tab="all">All invoices <span class="badge" data-count="all" style="display:none;"></span></button>
      <button class="btn" data-tab="receipts">Receipts to validate <span class="badge" data-count="receipts" style="display:none;"></span></button>
      <button class="btn ghost" data-bulk-release style="margin-left:auto; display:none;">Release all matched</button>
    </div>
    <div class="table-wrap"><table class="crm-inbox-table">
      <thead data-role="head"></thead>
      <tbody data-role="body"><tr><td colspan="6" class="crm-detail-loading">Loading…</td></tr></tbody>
    </table></div>`;
  container.querySelector('[data-review-refresh]').addEventListener('click', () => load(container));
  container.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => {
    currentTab = b.dataset.tab;
    load(container);
  }));
  container.querySelector('[data-bulk-release]').addEventListener('click', () => bulkRelease(container));
  container.querySelector('[data-role="body"]').addEventListener('click', (ev) => onRowClick(ev, container));
  await load(container);
}

function setActiveTab(container) {
  container.querySelectorAll('[data-tab]').forEach((b) =>
    b.classList.toggle('primary', b.dataset.tab === currentTab));
  container.querySelector('[data-bulk-release]').style.display = currentTab === 'receipts' ? '' : 'none';
}

async function load(container) {
  setActiveTab(container);
  const body = container.querySelector('[data-role="body"]');
  const head = container.querySelector('[data-role="head"]');
  body.innerHTML = '<tr><td colspan="6" class="crm-detail-loading">Loading…</td></tr>';
  try {
    if (currentTab === 'invoices') {
      head.innerHTML = '<tr><th>Invoice</th><th>Customer</th><th>Period</th><th>Total</th><th>Requested</th><th></th></tr>';
      const data = await api.fetch(API.invoicingApprovalsInbox('status=pending'), { method: 'GET' });
      pendingApprovals = data.items || [];
      renderInvoices(body, pendingApprovals);
      setCount(container, 'invoices', pendingApprovals.length);
    } else if (currentTab === 'all') {
      head.innerHTML = '<tr><th>Invoice</th><th>Customer</th><th>Segment</th><th>Total</th><th>Status</th><th></th></tr>';
      const data = await api.fetch(API.invoicingInvoices('kind=invoice'), { method: 'GET' });
      allInvoices = data.items || [];
      renderAll(body, allInvoices);
      setCount(container, 'all', allInvoices.length);
    } else {
      head.innerHTML = '<tr><th>Receipt</th><th>Customer</th><th>Period</th><th>Amount</th><th>Issued</th><th></th></tr>';
      const data = await api.fetch(API.invoicingInvoices('kind=receipt&status=draft'), { method: 'GET' });
      renderReceipts(body, data.items || []);
      setCount(container, 'receipts', (data.items || []).length);
    }
  } catch (err) {
    body.innerHTML = '<tr><td colspan="6" class="crm-detail-error">Failed to load.</td></tr>';
  }
}

function renderInvoices(body, items) {
  if (!items.length) { body.innerHTML = '<tr><td colspan="6" class="crm-detail-empty">Nothing awaiting approval. ✅</td></tr>'; return; }
  body.innerHTML = items.map((it) => {
    const self = it.self_approved ? ' <span class="badge warn" title="Self-approved — flagged for audit">self</span>' : '';
    // Why it needs approval: a corporate customer, or raised by non-root staff.
    const reason = it.kind === 'creator'
      ? ' <span class="badge" title="Raised by someone other than root or a finance manager">Staff-raised</span>'
      : ' <span class="badge" title="Corporate invoices always need approval">Corporate</span>';
    return `<tr data-id="${escapeHtml(it.invoice_id)}" data-action="approve">
      <td><strong>${escapeHtml(it.number || '—')}</strong>${reason}${self}</td>
      <td>${escapeHtml(it.customer_name || '—')}</td>
      <td class="crm-detail-sub">${escapeHtml(it.period || '—')}</td>
      <td>${money('GMD', it.total)}</td>
      <td class="crm-detail-sub">${escapeHtml(it.requested_at ? formatDateTimeShort(it.requested_at) : '')}</td>
      <td style="white-space:nowrap; text-align:right;">
        <button class="btn ghost" data-expand title="Show the customer's billing status">▸ Billing</button>
        <button class="btn" data-preview title="Review the invoice before deciding">Preview</button>
        <button class="btn primary" data-approve>Approve</button>
        <button class="btn ghost" data-reject>Reject</button>
      </td></tr>`;
  }).join('');
}

// Map a reminder-style approval item onto the minimal invoice shape the preview
// dialog needs (it is always a pending invoice on this tab).
function invoiceFromApproval(it) {
  return {
    id: it.invoice_id, number: it.number, kind: 'invoice', status: 'awaiting_approval',
    segment: it.segment, currency: it.currency, pending_approval: true, pending_gate: true,
  };
}

const BILL_STATUS = { O: 'Open', S: 'Suspended', C: 'Closed' };

function renderBillingPanel(b, cur) {
  if (!b || b.linked === false) {
    return '<span class="crm-detail-sub">No PortaBilling account is linked to this customer.</span>';
  }
  if (b.linked == null) {
    return '<span class="crm-detail-sub">Billing status is unavailable right now — try again shortly.</span>';
  }
  const currency = b.currency || cur;
  const state = BILL_STATUS[b.bill_status] || b.bill_status || '—';
  const blocked = b.blocked ? ' <span class="badge danger">Blocked</span>' : '';
  const type = b.is_postpaid ? 'Postpaid' : (b.control_type === 2 ? 'Prepaid' : '—');
  const rows = [
    ['Account status', `${escapeHtml(state)}${blocked}`],
    ['Billing type', escapeHtml(type)],
    ['Balance', money(currency, b.balance)],
  ];
  if (b.is_postpaid) {
    rows.push(['Overdue', money(currency, b.overdue_balance)]);
    if (b.credit_limit != null) rows.push(['Credit limit', money(currency, b.credit_limit)]);
  }
  if (b.unallocated_payments) rows.push(['Unapplied payments', money(currency, b.unallocated_payments)]);
  if (b.next_billed_to) rows.push(['Next bill', escapeHtml(String(b.next_billed_to).slice(0, 10))]);
  const cells = rows.map(([k, v]) =>
    `<div class="approval-billing-item"><div class="label">${escapeHtml(k)}</div><div class="value">${v}</div></div>`).join('');
  return `<div class="approval-billing">${cells}</div>`;
}

// Toggle an expandable billing panel under an approval row (lazy-loaded, cached).
async function toggleBillingExpand(row, btn, container) {
  const next = row.nextElementSibling;
  if (next && next.classList.contains('approval-detail-row')) { next.remove(); btn.textContent = '▸ Billing'; return; }
  const it = pendingApprovals.find((x) => String(x.invoice_id) === String(row.dataset.id));
  const pid = it && it.porta_customer_id;
  const cur = (it && it.currency) || 'GMD';
  const tr = document.createElement('tr');
  tr.className = 'approval-detail-row';
  tr.innerHTML = '<td colspan="6" class="approval-detail-cell"><div class="crm-detail-loading">Loading billing…</div></td>';
  row.after(tr);
  btn.textContent = '▾ Billing';
  const cell = tr.firstElementChild;
  if (pid == null) { cell.innerHTML = '<span class="crm-detail-sub">No linked billing account.</span>'; return; }
  try {
    let b = billingCache.get(pid);
    if (!b) { b = await api.fetch(API.invoicingCustomerBilling(pid), { method: 'GET' }); billingCache.set(pid, b); }
    cell.innerHTML = renderBillingPanel(b, cur);
  } catch (err) {
    cell.innerHTML = '<span class="crm-detail-error">Billing unavailable.</span>';
  }
}

function renderAll(body, items) {
  if (!items.length) { body.innerHTML = '<tr><td colspan="6" class="crm-detail-empty">No invoices generated yet.</td></tr>'; return; }
  body.innerHTML = items.map((it) => {
    const notInvoiceOrGone = it.kind === 'receipt' || it.status === 'void';
    const del = (!notInvoiceOrGone && it.status !== 'sent')
      ? '<button class="btn danger ghost" data-delete title="Delete this unsent invoice">Delete</button>'
      : (!notInvoiceOrGone && it.status === 'sent'
        ? '<button class="btn danger ghost" data-void title="Void this invoice so a corrected one can be issued">Void</button>' : '');
    return `<tr data-id="${escapeHtml(it.id)}">
      <td><strong>${escapeHtml(it.number || '—')}</strong></td>
      <td>${escapeHtml(it.customer_name || ('#' + it.porta_customer_id))}</td>
      <td class="crm-detail-sub">${escapeHtml(it.segment || 'home')}</td>
      <td>${money(it.currency, it.total)}</td>
      <td>${statusChip(it)}</td>
      <td style="white-space:nowrap; text-align:right;"><button class="btn" data-preview>Preview</button> ${del}</td>
    </tr>`;
  }).join('');
}

function renderReceipts(body, items) {
  if (!items.length) { body.innerHTML = '<tr><td colspan="6" class="crm-detail-empty">No receipts awaiting validation.</td></tr>'; return; }
  body.innerHTML = items.map((it) => {
    const period = (it.period) || '—';
    return `<tr data-id="${escapeHtml(it.id)}" data-action="release">
      <td><strong>${escapeHtml(it.number || '—')}</strong></td>
      <td>${escapeHtml(it.customer_name || '—')}</td>
      <td class="crm-detail-sub">${escapeHtml(period)}</td>
      <td>${money(it.currency, it.total)}</td>
      <td class="crm-detail-sub">${escapeHtml(it.issued_at ? formatDateTimeShort(it.issued_at) : '')}</td>
      <td style="white-space:nowrap;">
        <button class="btn primary" data-release>Release</button>
      </td></tr>`;
  }).join('');
}

async function onRowClick(ev, container) {
  const row = ev.target.closest('tr[data-id]');
  if (!row) return;
  const id = row.dataset.id;
  if (ev.target.closest('[data-expand]')) {
    await toggleBillingExpand(row, ev.target.closest('[data-expand]'), container);
    return;
  }
  if (ev.target.closest('[data-preview]')) {
    // Approvals tab items are keyed by invoice_id and carry no full invoice; the
    // "All invoices" tab holds full invoice objects keyed by id.
    let inv = null;
    if (currentTab === 'invoices') {
      const fromApproval = pendingApprovals.find((x) => String(x.invoice_id) === String(id));
      if (fromApproval) inv = invoiceFromApproval(fromApproval);
    } else {
      inv = allInvoices.find((x) => String(x.id) === String(id));
    }
    if (inv) openInvoicePreview(inv, { onChanged: () => load(container) });
    return;
  }
  if (ev.target.closest('[data-delete]')) {
    if (!window.confirm('Delete this unsent invoice? This cannot be undone.')) return;
    try {
      await api.fetch(API.invoicingInvoice(id), { method: 'DELETE' });
      flash('Invoice deleted.', 'success');
      await load(container);
    } catch (err) {
      flash(api.formatErrorDetail(err) || 'Delete failed', 'error');
    }
    return;
  }
  if (ev.target.closest('[data-void]')) {
    if (!window.confirm('Void this invoice? It stays on record as voided; a corrected one can then be generated.')) return;
    try {
      await api.fetch(API.invoicingVoid(id), { method: 'POST' });
      flash('Invoice voided.', 'success');
      await load(container);
    } catch (err) {
      flash(api.formatErrorDetail(err) || 'Void failed', 'error');
    }
    return;
  }
  if (ev.target.closest('[data-approve]') || ev.target.closest('[data-reject]')) {
    const approve = !!ev.target.closest('[data-approve]');
    let note = '';
    if (!approve) { note = window.prompt('Reason for rejecting (required):', '') || ''; if (!note.trim()) return; }
    else { note = window.prompt('Approval note (optional):', '') || ''; }
    await decide(id, approve, note, container);
  } else if (ev.target.closest('[data-release]')) {
    await release(id, container);
  }
}

async function decide(id, approve, note, container) {
  try {
    await api.fetch(API.invoicingDecide(id), {
      method: 'POST', body: JSON.stringify({ decision: approve ? 'approve' : 'reject', note: note || null }),
    });
    flash(approve ? 'Approved.' : 'Rejected.', 'success');
    await load(container);
    refreshBadge();
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Decision failed', 'error');
  }
}

async function release(id, container) {
  try {
    await api.fetch(API.invoicingRelease(id), { method: 'POST', body: JSON.stringify({}) });
    flash('Receipt released.', 'success');
    await load(container);
  } catch (err) {
    flash(api.formatErrorDetail(err) || 'Release failed', 'error');
  }
}

async function bulkRelease(container) {
  const ids = Array.from(container.querySelectorAll('tr[data-id][data-action="release"]')).map((r) => r.dataset.id);
  if (!ids.length) { flash('No receipts to release.', 'info'); return; }
  if (!window.confirm(`Release ${ids.length} receipt(s)?`)) return;
  let ok = 0;
  for (const id of ids) {
    try { await api.fetch(API.invoicingRelease(id), { method: 'POST', body: JSON.stringify({}) }); ok += 1; }
    catch { /* keep going */ }
  }
  flash(`Released ${ok} of ${ids.length}.`, ok === ids.length ? 'success' : 'error');
  await load(container);
}

function setCount(container, tab, n) {
  const el = container.querySelector(`[data-count="${tab}"]`);
  if (el) { el.textContent = n > 0 ? String(n) : ''; el.style.display = n > 0 ? '' : 'none'; }
  if (tab === 'invoices') setNavBadge(n);
}

function setNavBadge(n) {
  const badge = document.getElementById('invoicingReviewBadge');
  if (!badge) return;
  badge.textContent = n > 0 ? String(n) : '';
  badge.style.display = n > 0 ? '' : 'none';
}

export async function refreshBadge() {
  try {
    const data = await api.fetch(API.invoicingApprovalsInbox('status=pending'), { method: 'GET' });
    setNavBadge((data.items || []).length);
  } catch (err) { /* non-fatal */ }
}

export default { mountReview, refreshBadge };
